import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";

// Pages
import Welcome from "./pages/Welcome";
import Onboarding from "./pages/Onboarding";
import Home from "./pages/Home";
import Session from "./pages/Session";
import Chat from "./pages/Chat";
import Library from "./pages/Library";
import Journal from "./pages/Journal";
import Progress from "./pages/Progress";
import Settings from "./pages/Settings";
import Invite from "./pages/Invite";
import Donate from "./pages/Donate";
import BibleReader from "./pages/BibleReader";

// Fonts
import "@fontsource/playfair-display/400.css";
import "@fontsource/playfair-display/500.css";
import "@fontsource/playfair-display/600.css";
import "@fontsource/playfair-display/700.css";
import "@fontsource/lato/300.css";
import "@fontsource/lato/400.css";
import "@fontsource/lato/700.css";
import "@fontsource/caveat/400.css";
import "@fontsource/caveat/500.css";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-gentle-pulse">
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
            <span className="text-3xl">🙏</span>
          </div>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

const OnboardingGuard = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-gentle-pulse">
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
            <span className="text-3xl">🙏</span>
          </div>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/" replace />;
  }
  
  if (!user.onboarding_complete) {
    return <Navigate to="/onboarding" replace />;
  }
  
  return children;
};

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="min-h-screen bg-background">
            <Routes>
              <Route path="/" element={<Welcome />} />
            <Route 
              path="/onboarding" 
              element={
                <ProtectedRoute>
                  <Onboarding />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/home" 
              element={
                <OnboardingGuard>
                  <Home />
                </OnboardingGuard>
              } 
            />
            <Route 
              path="/session" 
              element={
                <OnboardingGuard>
                  <Session />
                </OnboardingGuard>
              } 
            />
            <Route 
              path="/chat" 
              element={
                <OnboardingGuard>
                  <Chat />
                </OnboardingGuard>
              } 
            />
            <Route 
              path="/library" 
              element={
                <OnboardingGuard>
                  <Library />
                </OnboardingGuard>
              } 
            />
            <Route 
              path="/journal" 
              element={
                <OnboardingGuard>
                  <Journal />
                </OnboardingGuard>
              } 
            />
            <Route 
              path="/progress" 
              element={
                <OnboardingGuard>
                  <Progress />
                </OnboardingGuard>
              } 
            />
            <Route 
              path="/settings" 
              element={
                <OnboardingGuard>
                  <Settings />
                </OnboardingGuard>
              } 
            />
            <Route 
              path="/invite" 
              element={
                <OnboardingGuard>
                  <Invite />
                </OnboardingGuard>
              } 
            />
            <Route 
              path="/donate" 
              element={
                <OnboardingGuard>
                  <Donate />
                </OnboardingGuard>
              } 
            />
            <Route 
              path="/donate/success" 
              element={
                <OnboardingGuard>
                  <Donate />
                </OnboardingGuard>
              } 
            />
            <Route 
              path="/bible" 
              element={
                <OnboardingGuard>
                  <BibleReader />
                </OnboardingGuard>
              } 
            />
          </Routes>
          <Toaster position="top-center" />
        </div>
      </BrowserRouter>
    </AuthProvider>
  </LanguageProvider>
  );
}

export default App;
